import { useId, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import {
  formatPeso,
  spendAmount,
  type CashFlow,
  type FundEntryLike,
  type IncomeItem,
} from '@tahi/core';
import { Money } from '../../../ui';
import { spokenPeso } from '../../../ui/spokenPeso';
import { copy } from '../copy';
import { dayShort } from '../format';
import { CARD, FOCUS, LABEL } from '../styles';
import { signedPeso } from './MoneyCards';

type PaydayItem = Extract<IncomeItem, { kind: 'payday' }>;

/** One tappable line; the items behind it open below (spec: each line can be tapped). */
function Line({
  label,
  minor,
  minus = false,
  note,
  children,
}: {
  label: string;
  minor: number;
  minus?: boolean;
  note?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <li className="py-1">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        aria-label={`${label}, ${spokenPeso(minor)}`}
        onClick={() => setOpen((o) => !o)}
        className={`flex min-h-11 w-full items-center justify-between gap-3 text-left ${FOCUS}`}
      >
        <span className="text-fg">
          {minus ? <span aria-hidden="true">− </span> : null}
          {label}
          <span aria-hidden="true" className="ml-1 text-muted">
            {open ? '▾' : '▸'}
          </span>
        </span>
        <Money minor={minor} cents="auto" animate={false} />
      </button>
      {note ? <p className="text-sm text-muted">{note}</p> : null}
      {open ? (
        <ul id={id} aria-label={copy.itemsOf(label)} className="mb-2 flex flex-col pl-3 text-sm">
          {children}
        </ul>
      ) : null}
    </li>
  );
}

function Item({
  label,
  minor,
  tag,
  to,
  muted = false,
}: {
  label: string;
  minor: number;
  tag?: string;
  to?: string;
  muted?: boolean;
}) {
  return (
    <li className="flex min-h-11 items-center justify-between gap-3">
      {to ? (
        <Link to={to} className={`min-w-0 text-primary ${FOCUS}`}>
          {label}
        </Link>
      ) : (
        <span className={`min-w-0 ${muted ? 'text-muted' : 'text-fg'}`}>{label}</span>
      )}
      <span className="flex shrink-0 items-center gap-2">
        {tag ? <span className="text-xs uppercase tracking-[0.06em] text-muted">{tag}</span> : null}
        <Money
          minor={minor}
          cents="auto"
          animate={false}
          className={muted ? 'text-muted' : undefined}
        />
      </span>
    </li>
  );
}

function Total({
  label,
  minor,
  strong = false,
}: {
  label: string;
  minor: number;
  strong?: boolean;
}) {
  return (
    <li
      aria-label={`${label}, ${minor < 0 ? 'minus ' : ''}${spokenPeso(Math.abs(minor))}`}
      className="flex min-h-11 items-center justify-between gap-3 py-1"
    >
      <span className={strong ? 'font-bold text-fg' : 'text-fg'}>{label}</span>
      <span
        className={`font-mono tabular-nums ${strong ? 'text-[20px] font-medium' : ''} ${minor < 0 ? 'text-destructive' : 'text-fg'}`}
      >
        {signedPeso(minor)}
      </span>
    </li>
  );
}

/** Received, expected, or (a past one never confirmed) not confirmed. */
function tagOf(i: PaydayItem, past: boolean): string {
  if (i.status === 'received') return copy.tagReceived;
  if (i.status === 'skipped') return copy.tagSkipped;
  return past ? copy.tagNotConfirmed : copy.tagExpected;
}

/**
 * One income total with its parts. Current and future periods: what is still expected (default
 * amounts) plus what came in. Past periods: received only, with salary and bonuses compared
 * like-for-like with their defaults, and other income on its own.
 */
function incomeNote(cf: CashFlow): string | undefined {
  if (cf.tense !== 'past') {
    if (cf.pendingIncomeMinor === 0 || cf.receivedIncomeMinor === 0) {
      return cf.pendingIncomeMinor > 0 ? copy.incomeParts(cf.pendingIncomeMinor, 0) : undefined;
    }
    return copy.incomeParts(cf.pendingIncomeMinor, cf.receivedIncomeMinor);
  }
  const parts: string[] = [];
  if (cf.expectedIncomeMinor > 0 || cf.paydaysReceivedMinor > 0) {
    parts.push(
      copy.paydaysVsExpected(cf.paydaysReceivedMinor, cf.expectedIncomeMinor),
      copy.vsExpected(cf.paydaysReceivedMinor - cf.expectedIncomeMinor),
    );
  }
  if (cf.otherIncomeMinor > 0) parts.push(copy.otherIncomeTotal(cf.otherIncomeMinor));
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

/**
 * The cash flow of a cutoff or month (v0.5, Ivan): every income source on its own line with
 * expected or received marked, each group of payments expandable to its items, and no line whose
 * amount is zero. Coming in (expected money that isn't income) is listed apart, never counted.
 */
export function CashFlowView({ cf, entries }: { cf: CashFlow; entries: readonly FundEntryLike[] }) {
  const byId = new Map(entries.map((e) => [e.id, e]));
  const label =
    cf.tense === 'future' ? copy.projection : cf.tense === 'past' ? copy.history : copy.soFar;
  const past = cf.tense === 'past';
  const incomeItems = cf.incomeItems.filter((i) =>
    i.kind === 'payday'
      ? i.status !== 'skipped' && (i.receivedMinor ?? i.defaultMinor) > 0
      : i.receivedMinor > 0,
  );
  const lines = cf.commitments
    .map((l) => ({ ...l, items: l.items.filter((i) => i.amountMinor > 0) }))
    .filter((l) => l.totalMinor > 0);
  const note = incomeNote(cf);

  return (
    <section aria-label={copy.cashFlow} className={CARD}>
      <p className={LABEL}>{label}</p>
      <ul className="mt-1 divide-y divide-divider">
        <li className="py-1">
          <div
            aria-label={`${copy.income}, ${spokenPeso(cf.incomeMinor)}`}
            className="flex min-h-11 items-center justify-between gap-3"
          >
            <span className="font-medium text-fg">{copy.income}</span>
            <Money minor={cf.incomeMinor} cents="auto" animate={false} />
          </div>
          {note ? <p className="text-sm text-muted">{note}</p> : null}
          {incomeItems.length === 0 ? (
            <p className="py-2 text-sm text-muted">{copy.noIncomeInPeriod}</p>
          ) : (
            <ul aria-label={copy.itemsOf(copy.income)} className="flex flex-col pl-3 text-sm">
              {incomeItems.map((i) =>
                i.kind === 'payday' ? (
                  <Item
                    key={i.id}
                    label={`${i.label} · ${dayShort(i.date)}`}
                    minor={i.receivedMinor ?? i.defaultMinor}
                    tag={tagOf(i, past)}
                    muted={i.countedMinor === 0}
                  />
                ) : (
                  <Item
                    key={i.id}
                    label={`${i.label ?? copy.otherIncomeLabel} · ${dayShort(i.date)}`}
                    minor={i.receivedMinor}
                    tag={copy.tagOther}
                  />
                ),
              )}
            </ul>
          )}
        </li>

        {lines.map((l) => (
          <Line key={l.key} minus label={l.label} minor={l.totalMinor}>
            {l.items.map((it) => (
              <Item
                key={it.id}
                label={`${it.label} · ${dayShort(it.date)}`}
                minor={it.amountMinor}
                tag={
                  it.paid
                    ? copy.tagPaid
                    : it.overdue
                      ? copy.tagOverdue
                      : it.estimated
                        ? copy.tagEstimated
                        : undefined
                }
              />
            ))}
          </Line>
        ))}
        {lines.length > 0 ? (
          <Total label={copy.leftAfterCommitments} minor={cf.leftAfterCommitmentsMinor} />
        ) : null}

        {cf.otherSpendingMinor !== 0 ? (
          <Line minus label={copy.otherSpending} minor={cf.otherSpendingMinor}>
            {cf.otherSpendingIds.map((id) => {
              const e = byId.get(id);
              if (!e) return null;
              return (
                <Item
                  key={id}
                  label={`${e.note ?? formatPeso(e.amountMinor)} · ${dayShort(e.date)}`}
                  minor={spendAmount(e)}
                  to={`/m/spending/entries/${id}`}
                />
              );
            })}
          </Line>
        ) : null}

        {cf.toSavingsMinor !== 0 ? (
          <Line minus label={copy.toSavings} minor={cf.toSavingsMinor} note={copy.toSavingsNote}>
            {cf.toSavings.map((i) => (
              <Item
                key={i.id}
                label={`${i.label ?? copy.toSavings} · ${dayShort(i.date)}`}
                minor={i.countedMinor}
              />
            ))}
          </Line>
        ) : null}

        <Total strong label={copy.whatYouHaveLeft} minor={cf.whatsLeftMinor} />
        {cf.whatsLeftMinor < 0 ? (
          <li className="pb-2 text-sm text-muted">{copy.shortNote(-cf.whatsLeftMinor)}</li>
        ) : null}

        {cf.planned.map((l) => (
          <Line key={l.key} label={l.label} minor={l.totalMinor} note={copy.plannedNote}>
            {l.items.map((it) => (
              <Item key={it.id} label={it.label} minor={it.amountMinor} />
            ))}
          </Line>
        ))}
      </ul>
      {cf.tense !== 'past' && cf.incomeMinor !== cf.receivedIncomeMinor ? (
        <p className="mt-2 text-sm text-muted">{copy.pendingNote}</p>
      ) : null}

      {cf.comingIn.length > 0 ? (
        <div className="mt-4 border-t border-divider pt-3">
          <div
            aria-label={`${copy.comingIn}, ${spokenPeso(cf.comingInMinor)}`}
            className="flex min-h-11 items-center justify-between gap-3"
          >
            <span className="font-medium text-fg">{copy.comingIn}</span>
            <Money minor={cf.comingInMinor} cents="auto" animate={false} className="text-muted" />
          </div>
          <p className="text-sm text-muted">{copy.comingInNote}</p>
          <ul aria-label={copy.itemsOf(copy.comingIn)} className="flex flex-col pl-3 text-sm">
            {cf.comingIn.map((c) => (
              <Item
                key={c.key}
                label={`${c.name} · ${dayShort(c.date)}`}
                minor={c.amountMinor}
                to={`/m/budget/expected/${c.id}`}
                tag={
                  c.status === 'received'
                    ? copy.tagReceived
                    : c.late
                      ? copy.tagLate
                      : copy.tagExpected
                }
              />
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
